import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ArrowsClockwise, ArrowSquareOut, ChartLine } from "@phosphor-icons/react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * Trafikstatistik för branschlandningssidorna — samma layout som Ads/Reels-panelen.
 * Intern tracking (POST /api/landing-stats/track) — inga cookies, ingen tredjepart.
 * Endast synlig för super_admin.
 */
export default function LandingStatsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE}/api/landing-stats/summary?days=${days}`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunde inte hämta statistik");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || {};
  const items = data?.items || [];
  const daily = data?.daily || [];
  const trackingStart = data?.tracking_start_date;

  return (
    <div className="mb-6 border border-app rounded-lg p-4 bg-white/5" data-testid="landing-stats-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <ChartLine size={18} weight="bold" className="text-brand" />
          <h3 className="font-heading text-base">Trafikstatistik</h3>
          <span className="text-[10px] text-text-muted">
            Intern tracking • inga cookies • bot-filter
            {trackingStart ? ` • start ${trackingStart}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="landing-stats-period"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border border-app rounded px-2 py-1 bg-transparent"
          >
            <option value={1}>Idag</option>
            <option value={7}>Senaste 7 dagar</option>
            <option value={30}>Senaste 30 dagar</option>
            <option value={90}>Senaste 90 dagar</option>
            {data?.days_since_start > 90 && (
              <option value={data.days_since_start}>
                Sedan start ({data.days_since_start} dagar)
              </option>
            )}
          </select>
          <button
            data-testid="landing-stats-reload"
            onClick={load}
            disabled={loading}
            className="text-xs px-2 py-1 border border-app rounded hover:bg-white/5 inline-flex items-center gap-1"
          >
            <ArrowsClockwise size={12} weight="bold" />
            {loading ? "Läser…" : "Uppdatera"}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 mb-3" data-testid="landing-stats-error">{error}</div>
      )}

      {/* Sammanfattning — 4 mätvärden som matchar Ads-panelen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Visningar" value={fmt(totals.views)} />
        <Stat label="Unika" value={fmt(totals.unique_visitors)} />
        <Stat label="Klick" value={fmt(totals.cta_total)} />
        <Stat label="Konvertering" value={`${(totals.cvr_percent || 0).toFixed(2)} %`} />
      </div>

      {/* Per-sida tabell — samma stil som daglig historik */}
      <div className="overflow-x-auto" data-testid="landing-stats-table">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted border-b border-app">
              <th className="py-1 pr-2">Landningssida</th>
              <th className="py-1 pr-2 text-right">Visningar</th>
              <th className="py-1 pr-2 text-right">Idag</th>
              <th className="py-1 pr-2 text-right">Igår</th>
              <th className="py-1 pr-2 text-right">Unika</th>
              <th className="py-1 pr-2 text-right">Prova gratis</th>
              <th className="py-1 pr-2 text-right">Boka demo</th>
              <th className="py-1 pr-2 text-right">Konvertering</th>
              <th className="py-1 pr-2">Top-källor</th>
              <th className="py-1 text-right">Öppna</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={10} className="py-4 text-center text-text-muted" data-testid="landing-stats-empty">
                  Ingen trafik registrerad ännu de senaste {days} dagarna.
                </td>
              </tr>
            )}
            {items.map((r) => (
              <tr key={r.slug} className="border-b border-app/50 hover:bg-white/5" data-testid={`landing-stats-row-${r.slug}`}>
                <td className="py-1 pr-2">
                  <div className="font-medium">{r.display_name}</div>
                  <div className="text-[10px] text-text-muted font-mono">{r.slug}</div>
                </td>
                <td className="py-1 pr-2 text-right font-mono">{fmt(r.views_total)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmt(r.views_today)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmt(r.views_yesterday)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmt(r.unique_visitors)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmt(r.cta_primary)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmt(r.cta_secondary)}</td>
                <td className="py-1 pr-2 text-right font-mono">{(r.cvr_percent || 0).toFixed(2)}%</td>
                <td className="py-1 pr-2 text-[10px] text-text-muted">
                  {r.top_sources?.length
                    ? r.top_sources.map((s) => `${s.source} (${s.count})`).join(", ")
                    : "—"}
                </td>
                <td className="py-1 text-right">
                  <a
                    href={`https://app.trappkonsulten.se/tidrapportering-${r.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid={`landing-stats-open-${r.slug}`}
                  >
                    <ArrowSquareOut size={12} weight="bold" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Daglig historik — matchar Ads-panelen */}
      <div className="mt-4 border-t border-app pt-4">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-sm font-medium">📅 Daglig historik ({daily.length} rader)</div>
          <div className="text-[10px] text-text-muted">
            Datum i UTC • Prova gratis + Boka demo räknas som konvertering
          </div>
        </div>
        {daily.length === 0 ? (
          <div className="text-xs text-text-muted p-3 bg-white/5 rounded" data-testid="landing-daily-empty">
            Ingen daglig historik än — dyker upp så fort tracking loggar sitt första besök i produktion.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="landing-daily-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted border-b border-app">
                  <th className="py-1 pr-2">Datum</th>
                  <th className="py-1 pr-2">Bransch</th>
                  <th className="py-1 pr-2 text-right">Visningar</th>
                  <th className="py-1 pr-2 text-right">Unika</th>
                  <th className="py-1 pr-2 text-right">Prova gratis</th>
                  <th className="py-1 pr-2 text-right">Boka demo</th>
                  <th className="py-1 text-right">Konvertering</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((r) => (
                  <tr key={`${r.date}-${r.slug}`} className="border-b border-app/50 hover:bg-white/5" data-testid={`landing-daily-row-${r.date}-${r.slug}`}>
                    <td className="py-1 pr-2 font-mono">{r.date}</td>
                    <td className="py-1 pr-2">{r.display_name}</td>
                    <td className="py-1 pr-2 text-right font-mono">{fmt(r.views)}</td>
                    <td className="py-1 pr-2 text-right font-mono">{fmt(r.unique)}</td>
                    <td className="py-1 pr-2 text-right font-mono">{fmt(r.cta_primary)}</td>
                    <td className="py-1 pr-2 text-right font-mono">{fmt(r.cta_secondary)}</td>
                    <td className="py-1 text-right font-mono">{(r.cvr_percent || 0).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("sv-SE");
}

function Stat({ label, value }) {
  return (
    <div className="bg-app border border-app rounded p-2">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className="text-sm font-mono mt-1">{value ?? "—"}</div>
    </div>
  );
}
